import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { VendorState } from "../../../../redux/vendor/rootReducer";
import { useTranslation } from "react-i18next";
import { PlankRequirements } from "../models/PlankRequirements.model";
import { useTextureApi } from "../../../../common/hooks/use-texture-api.hook";
import { setLoading } from "../../../../redux/vendor/ui/uiActions";
import {
    GroutColorField,
    PatternFieldLabel,
    PatternSectionTitle,
    SliderWithInput,
} from "./PatternFormControls";

interface SizeField {
  /** Starting value, in cm */
  value: string;
  min: number;
  max: number;
  /** i18n key under PATTERNS.PARTIALS */
  labelKey?: string;
}

interface PlankPatternFormProps {
  /** Pattern name the API and worker know it by (also the hidden button's id prefix) */
  pattern: string;
  width: SizeField;
  height: SizeField;
  /** Starting grout width, in mm (default 2) */
  grout?: string;
  seamlessResult: any;
  setSeamlessResult: (result: any) => void;
  uploadedImages?: File[];
}

/** Generator form for patterns sized by one plank or tile: width, height, texture, grout. */
export const PlankPatternForm = ({pattern, width, height, grout = "2", seamlessResult, setSeamlessResult, uploadedImages = []}: PlankPatternFormProps) => {
  const { t } = useTranslation();
  const { language } = useSelector((state: VendorState) => state.settings.theme);
  const [isOnEditGroutColor, setIsOnEditGroutColor] = useState(false);
  const textureAPI = useTextureApi();
  const dispatch = useDispatch();
  const [fields, setFields] = useState({
      plankWidth: {
          isTouched: false,
          label: `${t(`PATTERNS.PARTIALS.${width.labelKey ?? 'PLANK_WIDTH'}`)}`,
          name: "plankWidth",
          value: width.value,
          unit: "cm"
      },
      plankHeight: {
          isTouched: false,
          label: `${t(`PATTERNS.PARTIALS.${height.labelKey ?? 'PLANK_HEIGHT'}`)}`,
          name: "plankHeight",
          value: height.value,
          unit: "cm"
      },
      textureWidth: {
          isTouched: false,
          label: `${t('PATTERNS.PARTIALS.TEXTURE_WIDTH')}`,
          name: "textureWidth",
          value: "2",
          unit: "m"
      },
      textureHeight: {
          isTouched: false,
          label: `${t('PATTERNS.PARTIALS.TEXTURE_HEIGHT')}`,
          name: "textureHeight",
          value: "2",
          unit: "m"
      },
      groutLine: {
        isTouched: false,
        label: `${t('PATTERNS.PARTIALS.GROUT_LINE_WIDTH')}`,
        name: "groutLine",
        value: grout,
        unit: "mm"
      },
      groutColor: {
          isTouched: false,
          label: `${t('PATTERNS.PARTIALS.GROUT_LINE_COLOR')}`,
          name: "groutColor",
          value: "#CCCCCC"
      }
  });

  const handleChange = (event) => {
      const { name, value } = event.target;
      setFields({...fields, [name]: {
          ...fields[name],
          value: value,
          isTouched: true
      }});
  };

  const handleSliderChange = (name, value) => {
    setFields({...fields, [name]: {
      ...fields[name],
      value: value.toString(),
      isTouched: true
    }});
  };

  const isValidForm = () => {
    return uploadedImages && uploadedImages.length > 0 &&
      fields.plankWidth.value &&
      fields.plankHeight.value &&
      fields.textureWidth.value &&
      fields.textureHeight.value;
  };

  const handleGenerateSeamless = async () => {
    if(isValidForm()){
      try {
        dispatch(setLoading(true));
        const body = new PlankRequirements();
        body.setFiles(uploadedImages)
            .setPlankWidth(fields.plankWidth.value)
            .setPlankHeight(fields.plankHeight.value)
            .setTextureWidth(fields.textureWidth.value)
            .setTextureHeight(fields.textureHeight.value)
            .setGroutLineColor(fields.groutColor.value || "#FFFFFF")
            .setGroutLineWidth(fields.groutLine.value);

        let data = await textureAPI.generateTextureForPattern(body, pattern);
        setSeamlessResult(data);
      } catch (error) {
        console.error('Error generating seamless texture:', error);
        // Don't set seamlessResult on error, so modal won't open
        setSeamlessResult(null);
      } finally {
        dispatch(setLoading(false));
      }
    }
  };

  const handleGroutColorChange = (color) => {
    setFields({
      ...fields,
      groutColor: {
        ...fields.groutColor,
        value: color.hex + Math.round(color.rgb.a * 255).toString(16)
      }
    });
  };

  return (
    <div>
      {/* Hidden generate button that can be triggered programmatically */}
      <button
        type="button"
        id={`${pattern}-generate-btn`}
        onClick={handleGenerateSeamless}
        className="hidden"
      />

      <div className="mb-6">
        <PatternSectionTitle>{t('PATTERNS.PARTIALS.DIMENSIONS')}</PatternSectionTitle>

        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <div>
            <PatternFieldLabel>{fields.plankWidth.label}</PatternFieldLabel>
            <SliderWithInput
              name="plankWidth"
              value={fields.plankWidth.value}
              unit={fields.plankWidth.unit}
              min={width.min}
              max={width.max}
              step={1}
              onSliderChange={handleSliderChange}
              onInputChange={handleChange}
            />
          </div>

          <div>
            <PatternFieldLabel>{fields.plankHeight.label}</PatternFieldLabel>
            <SliderWithInput
              name="plankHeight"
              value={fields.plankHeight.value}
              unit={fields.plankHeight.unit}
              min={height.min}
              max={height.max}
              step={1}
              onSliderChange={handleSliderChange}
              onInputChange={handleChange}
            />
          </div>
        </div>
      </div>

      <div className="mt-8 mb-6">
        <PatternSectionTitle>{t('PATTERNS.PARTIALS.TEXTURE')}</PatternSectionTitle>

        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <div>
            <PatternFieldLabel>{fields.textureWidth.label}</PatternFieldLabel>
            <SliderWithInput
              name="textureWidth"
              value={fields.textureWidth.value}
              min={1}
              max={4}
              step={1}
              markLabels={['1m', '2m', '3m', '4m']}
              showInput={false}
              onSliderChange={handleSliderChange}
            />
          </div>

          <div>
            <PatternFieldLabel>{fields.textureHeight.label}</PatternFieldLabel>
            <SliderWithInput
              name="textureHeight"
              value={fields.textureHeight.value}
              min={1}
              max={4}
              step={1}
              markLabels={['1m', '2m', '3m', '4m']}
              showInput={false}
              onSliderChange={handleSliderChange}
            />
          </div>
        </div>
      </div>

      <div className="mt-8">
        <PatternSectionTitle>{t('PATTERNS.PARTIALS.GROUT')}</PatternSectionTitle>

        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <div>
            <PatternFieldLabel>{fields.groutLine.label}</PatternFieldLabel>
            <SliderWithInput
              name="groutLine"
              value={fields.groutLine.value}
              unit={fields.groutLine.unit}
              min={0}
              max={10}
              step={0.5}
              onSliderChange={handleSliderChange}
              onInputChange={handleChange}
            />
          </div>

          <div>
            <PatternFieldLabel>{fields.groutColor.label}</PatternFieldLabel>
            <GroutColorField
              color={fields.groutColor.value}
              open={isOnEditGroutColor}
              onOpenChange={setIsOnEditGroutColor}
              onColorChange={handleGroutColorChange}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
